import React, { PureComponent, useContext, useState } from 'react';
import { PieChart, Pie, Cell, Tooltip, ResponsiveContainer } from 'recharts';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';
import { SlideOptions, SlideOptionsWrapper } from '../../../../../../styles/components/SlideActions';
import { ReportStyleContext } from '../../../../context/ReportStyleContext';
import Snippet_Block_Overlay from '../snippets/Snippet_Block_Overlay';
import { Snippet_Vertical_Actions } from '../snippets/Snippet_Vertical_Actions';
import { device } from 'monica-alexandria';
import Edit_Block from '../edit/Edit_Block';

const BlockGender = styled(SlideOptions)`
    height: 23rem;
    width: 100%;
    background: ${(p) => p.theme.low};
    border-radius: ${(p) => p.presetStyle.radius};

    .recharts-surface,
    .recharts-responsive-container,
    .recharts-wrapper {
        cursor: pointer;
        position: relative;
    }

    @media ${device.lg} {
        height: 100%;
    }
`;

const BlockGenderWrapper = styled(SlideOptionsWrapper)`
    display: grid;
    grid-template-columns: 1fr auto;
    border: 1px solid ${(p) => p.presetStyle.color.background};
    background: ${(p) => (p.variant == 'main' ? p.presetStyle.color.main : p.variant == 'focus' ? p.presetStyle.color.focus : p.presetStyle.color.grey)};
    border-radius: ${(p) => p.presetStyle.radius};
    padding: 1rem 2rem;

    @media ${device.lg} {
        grid-template-columns: 1fr;
    }
`;

const BlockGenderPie = styled.div`
    height: 21rem;
    width: 21rem;
`;

const BlockGenderCaption = styled.div``;

const BlockGenderTitle = styled.h6`
    font-family: ${(p) => p.presetStyle.font.family};
    color: ${(p) => p.presetStyle.color.textDark} !important;
    margin-bottom: 2rem;
`;

const BlockGenderValue = styled.div`
    margin-top: 1rem;
    display: flex;
    gap: 0.5rem;
    align-items: center;

    p{
        color: ${(p) => p.presetStyle.color.textDark};
    }
`;

const BlockGenderLabel = styled.div`
    display: block;
    background: ${(p) => (p.male ? p.presetStyle.color.main : p.female ? p.presetStyle.color.focus : p.presetStyle.color.textLight)};
    height: 1.6rem;
    width: 2rem;
    border-radius: 0.5rem;
`;

export default function Block_Gender(props) {
    const { t } = useTranslation();

    const { reportStyle } = useContext(ReportStyleContext);
    const [slider, setSlider] = useState(false);


    const open_close_slider = () => {
        setSlider(false);
    };

    const COLORS = [reportStyle.color.focus, reportStyle.color.main, reportStyle.color.textLight];

    function formatYAxis(number) {
        if(!number)
            return number
        // Check if the number is one thousand or more but less than a million
        if (number >= 1000 && number < 1000000) {
            return (number / 1000).toFixed(1).replace(/\.0$/, '') + 'K';
        }
        // Check if the number is one million or more
        else if (number >= 1000000) {
            return (number / 1000000).toFixed(1).replace(/\.0$/, '') + 'M';
        }
        // Handle numbers less than 1000 with up to two decimal places
        else if (!Number.isInteger(number)) {
            return number.toFixed(2);
        }
        // Handle integer values under 1000
        return number;
    }

    return (
        <BlockGender actions={props.actions} presetStyle={reportStyle}>
            {slider ? 
            <Edit_Block 
                onClick={() => open_close_slider()} 
                onBack={() => open_close_slider()}
                title={props.title}       
                titlePlaceholder={props.titlePlaceholder} 
                titleDefaultValue={props.titleDefaultValue} 
                setBlock={props.setBlock} 
                id={props.id} 
                displayedName={props.kpiName}
                element={props.element} 
                sectionType={props.sectionType} 
                sectionId={props.sectionId} 
                rowType={props.rowType} 
            /> : null }
            <Snippet_Vertical_Actions onEdit={() => setSlider(!slider)} onDelete={props.onDelete} />
            <BlockGenderWrapper viewMode={props.viewMode} variant={props.variant} actions={props.actions} presetStyle={reportStyle} onClick={props.onActions}>
                <BlockGenderCaption>
                    <BlockGenderTitle presetStyle={reportStyle}>{props.kpiName}</BlockGenderTitle>
                    <BlockGenderValue presetStyle={reportStyle}>
                        <BlockGenderLabel presetStyle={reportStyle} male></BlockGenderLabel>
                        <p>
                            {t('Male')}: {formatYAxis(props.json?.find((item) => item?.[props.dataIdentifier] === 'male')?.value)}
                        </p>
                    </BlockGenderValue>
                    <BlockGenderValue presetStyle={reportStyle}>
                        <BlockGenderLabel presetStyle={reportStyle} female></BlockGenderLabel>
                        <p>
                            {t('Female')}: {formatYAxis(props.json?.find((item) => item?.[props.dataIdentifier] === 'female')?.value)}
                        </p>
                    </BlockGenderValue>
                    <BlockGenderValue presetStyle={reportStyle}>
                        <BlockGenderLabel presetStyle={reportStyle}></BlockGenderLabel>
                        <p>
                            {t('Unknown')}: {formatYAxis(props.json?.find((item) => item?.[props.dataIdentifier] === 'unknown')?.value)}
                        </p>
                    </BlockGenderValue>
                </BlockGenderCaption>
                <BlockGenderPie>
                    <ResponsiveContainer width="100%" height="100%">
                        <PieChart>
                            <Pie data={props.json} cx="50%" cy="50%" labelLine={false} outerRadius={100} fill="#8884d8" dataKey={props.dataKey}>
                                {props.json?.map((entry, index) => (
                                    <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                                ))}
                            </Pie>
                        </PieChart>
                    </ResponsiveContainer>
                </BlockGenderPie>
            </BlockGenderWrapper>
            {!props.viewMode ? <Snippet_Block_Overlay stylePreset={reportStyle} text={props.text} /> : null}
        </BlockGender>
    );
}
